import { currentOfflineAuthorizationEnvelope } from './authEnvelope';
import { getAllByIndex, idbRequest, withOfflineTransaction } from './db';

export interface ReferenceSnapshot<T = unknown> {
  local_key: string;
  tenant_id: string;
  user_id: string;
  dataset: string;
  record_id: string;
  value: T;
  version: string;
  version_source: 'SERVER' | 'UNVERSIONED_SNAPSHOT';
  effective_at: string;
  expires_at: string | null;
  last_synced_at: string;
}

export async function replaceReferenceDataset<T extends { id?: string }>(
  userId: string,
  dataset: string,
  records: T[],
  options: { version?: string | null; ttlMs?: number | null } = {},
): Promise<void> {
  const envelope = await currentOfflineAuthorizationEnvelope(userId);
  if (!envelope) throw new Error('Offline authorization envelope required before caching user-scoped reference data');
  const now = new Date();
  const version = options.version || `snapshot-${now.toISOString()}`;
  const expiresAt = options.ttlMs == null ? null : new Date(now.getTime() + options.ttlMs).toISOString();
  const existing = await getAllByIndex<ReferenceSnapshot<T>>('reference_data', 'byDataset', IDBKeyRange.only([envelope.tenant_id, userId, dataset]));
  await withOfflineTransaction(['reference_data'], 'readwrite', tx => {
    const store = tx.objectStore('reference_data');
    existing.forEach(row => store.delete(row.local_key));
    records.forEach((record, index) => {
      const recordId = String(record.id || index);
      const row: ReferenceSnapshot<T> = {
        local_key: `${envelope.tenant_id}:${userId}:${dataset}:${recordId}`,
        tenant_id: envelope.tenant_id,
        user_id: userId,
        dataset,
        record_id: recordId,
        value: record,
        version,
        version_source: options.version ? 'SERVER' : 'UNVERSIONED_SNAPSHOT',
        effective_at: now.toISOString(),
        expires_at: expiresAt,
        last_synced_at: now.toISOString(),
      };
      store.put(row);
    });
  });
}

export async function loadReferenceDataset<T>(userId: string, dataset: string, allowExpiredForRecovery = true): Promise<{ records: T[]; stale: boolean; version: string | null }> {
  const envelope = await currentOfflineAuthorizationEnvelope(userId);
  if (!envelope) return { records: [], stale: true, version: null };
  const rows = await getAllByIndex<ReferenceSnapshot<T>>('reference_data', 'byDataset', IDBKeyRange.only([envelope.tenant_id, userId, dataset]));
  if (!rows.length) return { records: [], stale: true, version: null };
  const now = Date.now();
  const expired = rows.some(row => row.expires_at != null && Date.parse(row.expires_at) <= now);
  if (expired && !allowExpiredForRecovery) return { records: [], stale: true, version: rows[0].version };
  return { records: rows.map(row => row.value), stale: expired, version: rows[0].version };
}
